// src/lib/kinema/core/handlers/layerHandlers.ts
import type { EditorState, InverseOperation, BaseLayerData } from '../types';
import { getCompositionProps } from '../types';
import { ADD_LAYER, REMOVE_LAYER, UPDATE_PROPERTY, MOVE_LAYER } from '../commands/commands';
import { LayerTypeRegistry } from '../layers/LayerTypeRegistry';
import { cloneState } from '../EditorState';
import { snapToFrame } from '../frameUtils';

let idCounter = 0;
function generateId(): string {
  return `layer-${Date.now()}-${++idCounter}`;
}

export function handleAddLayer(
  state: EditorState,
  payload: any,
  registry: LayerTypeRegistry,
): { state: EditorState; inverse: InverseOperation | null } {
  const newState = cloneState(state);
  const id = payload.id ?? generateId();
  const typeDefaults = registry.getDefaults(payload.type);

  // Build layer: base defaults → type defaults → payload overrides
  const base: BaseLayerData = {
    id,
    type: payload.type,
    name: payload.name ?? payload.type,
    order:
      payload.order ??
      Object.values(newState.layers).filter(
        (l) => l.parentId === (payload.parentId ?? state.rootLayerId),
      ).length,
    parentId: payload.parentId ?? state.rootLayerId,
    x: 0,
    y: 0,
    width: 100,
    height: 100,
    anchorX: 0,
    anchorY: 0,
    rotation: 0,
    scaleX: 1,
    scaleY: 1,
    opacity: 1,
    shadowColor: 'transparent',
    shadowBlur: 0,
    shadowOffsetX: 0,
    shadowOffsetY: 0,
    shadowOpacity: 0,
    startTime: 0,
    endTime: 5,
    visible: true,
    locked: false,
    effects: {},
    ...typeDefaults,
    ...payload,
  };

  // Compute anchor defaults from final width/height if not explicitly set
  if (payload.anchorX === undefined) base.anchorX = base.width / 2;
  if (payload.anchorY === undefined) base.anchorY = base.height / 2;

  // Default x/y to anchor position (places top-left at 0,0)
  if (payload.x === undefined) base.x = base.anchorX;
  if (payload.y === undefined) base.y = base.anchorY;

  newState.layers[id] = base;

  return {
    state: newState,
    inverse: { command: REMOVE_LAYER, payload: { layerId: id } },
  };
}

export function handleRemoveLayer(
  state: EditorState,
  payload: { layerId: string },
): { state: EditorState; inverse: InverseOperation | null } {
  const layer = state.layers[payload.layerId];
  if (!layer) {
    return { state, inverse: null };
  }

  const newState = cloneState(state);
  const snapshot = { ...layer };
  delete newState.layers[payload.layerId];

  // Remove from selection if selected
  newState.selectedIds.delete(payload.layerId);
  if (newState.primarySelectedId === payload.layerId) {
    newState.primarySelectedId = null;
  }

  return {
    state: newState,
    inverse: { command: ADD_LAYER, payload: snapshot },
  };
}

export function handleUpdateProperty(
  state: EditorState,
  payload: { layerId: string; property: string; value: any; _removeKeyframeAtTime?: number },
): { state: EditorState; inverse: InverseOperation | null } {
  const layer = state.layers[payload.layerId];
  if (!layer) {
    return { state, inverse: null };
  }

  const oldValue = layer[payload.property];
  const newState = cloneState(state);
  let workingLayer = { ...layer };
  let workingEffects = { ...layer.effects };

  // Step 0: If _removeKeyframeAtTime is set (undo path), remove that keyframe first
  if (payload._removeKeyframeAtTime !== undefined) {
    const effect = workingEffects[payload.property];
    if (effect) {
      workingEffects[payload.property] = {
        ...effect,
        keyframes: effect.keyframes.filter((kf) => kf.time !== payload._removeKeyframeAtTime),
      };
      workingLayer = { ...workingLayer, effects: workingEffects };
    }
  }

  // Step 1: Set the base property value
  workingLayer = { ...workingLayer, [payload.property]: payload.value };

  // Step 2: Auto-keyframe if an enabled effect exists for this property
  // Skip auto-keyframe if _removeKeyframeAtTime was set (this is an undo operation)
  const effect = workingEffects[payload.property];
  if (effect && effect.enabled && payload._removeKeyframeAtTime === undefined) {
    const { frameRate } = getCompositionProps(state);
    const localTime = state.playheadTime - layer.startTime;
    const snappedTime = snapToFrame(localTime, frameRate);
    const existingKfIndex = effect.keyframes.findIndex((kf) => kf.time === snappedTime);

    if (existingKfIndex >= 0) {
      // Update existing keyframe value
      const newKeyframes = effect.keyframes.map((kf, i) =>
        i === existingKfIndex ? { ...kf, value: payload.value } : kf,
      );
      workingEffects = {
        ...workingEffects,
        [payload.property]: { ...effect, keyframes: newKeyframes },
      };
      workingLayer = { ...workingLayer, effects: workingEffects };

      // Inverse: just UPDATE_PROPERTY with old value (symmetric — will auto-update KF back)
      newState.layers[payload.layerId] = workingLayer;
      return {
        state: newState,
        inverse: {
          command: UPDATE_PROPERTY,
          payload: { layerId: payload.layerId, property: payload.property, value: oldValue },
        },
      };
    } else {
      // Create new keyframe
      const newKeyframes = [
        ...effect.keyframes,
        { time: snappedTime, value: payload.value, easing: 'linear' as const },
      ].sort((a, b) => a.time - b.time);
      workingEffects = {
        ...workingEffects,
        [payload.property]: { ...effect, keyframes: newKeyframes },
      };
      workingLayer = { ...workingLayer, effects: workingEffects };

      // Inverse: UPDATE_PROPERTY with _removeKeyframeAtTime hint
      newState.layers[payload.layerId] = workingLayer;
      return {
        state: newState,
        inverse: {
          command: UPDATE_PROPERTY,
          payload: {
            layerId: payload.layerId,
            property: payload.property,
            value: oldValue,
            _removeKeyframeAtTime: snappedTime,
          },
        },
      };
    }
  }

  // No effect — standard behavior
  newState.layers[payload.layerId] = workingLayer;
  return {
    state: newState,
    inverse: {
      command: UPDATE_PROPERTY,
      payload: { layerId: payload.layerId, property: payload.property, value: oldValue },
    },
  };
}

export function handleMoveLayer(
  state: EditorState,
  payload: { layerId: string; newParentId: string | null; newOrder: number },
): { state: EditorState; inverse: InverseOperation | null } {
  const layer = state.layers[payload.layerId];
  if (!layer) {
    return { state, inverse: null };
  }

  const oldParentId = layer.parentId;
  const oldOrder = layer.order;

  const newState = cloneState(state);
  newState.layers[payload.layerId] = {
    ...layer,
    parentId: payload.newParentId,
    order: payload.newOrder,
  };

  return {
    state: newState,
    inverse: {
      command: MOVE_LAYER,
      payload: { layerId: payload.layerId, newParentId: oldParentId, newOrder: oldOrder },
    },
  };
}
